/**
 * LaundryWorkflowModal.js - Complete Laundry Service Workflow
 * Steps: Pickup → Count Items → Photos → Wash/Dry → Fold → Delivery → Signature → Complete
 */
import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronRight, ChevronLeft, MapPin, Clock, Camera, Pen, Upload,
  CheckCircle, AlertTriangle, User, Phone, FileText, Package, Shirt,
  Loader2, CheckSquare, Square, Navigation, Timer, Image, Truck
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const LAUNDRY_STEPS = {
  OVERVIEW: 'overview',
  PICKUP: 'pickup',
  ITEM_COUNT: 'item_count',
  PHOTOS_BEFORE: 'photos_before',
  IN_PROGRESS: 'in_progress',
  PHOTOS_AFTER: 'photos_after',
  DELIVERY: 'delivery',
  SIGNATURE: 'signature',
  COMPLETE: 'complete'
};

const LaundryWorkflowModal = ({ isOpen, onClose, booking, worker, onWorkflowComplete }) => {
  const [currentStep, setCurrentStep] = useState(LAUNDRY_STEPS.OVERVIEW);
  const [loading, setLoading] = useState(false);
  
  // Pickup state
  const [arrivedAtPickup, setArrivedAtPickup] = useState(false);
  const [pickupTime, setPickupTime] = useState(null);
  
  // Item count state
  const [items, setItems] = useState([
    { id: 1, type: 'Shirts/Tops', count: 0 },
    { id: 2, type: 'Pants/Jeans', count: 0 },
    { id: 3, type: 'Dresses/Skirts', count: 0 },
    { id: 4, type: 'Underwear/Socks', count: 0 },
    { id: 5, type: 'Towels', count: 0 },
    { id: 6, type: 'Bedding/Sheets', count: 0 },
    { id: 7, type: 'Delicates', count: 0 },
    { id: 8, type: 'Other', count: 0 }
  ]);
  const [totalItems, setTotalItems] = useState(0);
  const [itemNotes, setItemNotes] = useState('');
  
  // Photo state
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  // Progress checklist
  const [checklist, setChecklist] = useState([
    { id: 'sort_colors', label: 'Sort by colors (whites, darks, colors)', checked: false },
    { id: 'sort_fabric', label: 'Sort by fabric type (delicates separate)', checked: false },
    { id: 'check_pockets', label: 'Check all pockets', checked: false },
    { id: 'pretreat', label: 'Pre-treat stains', checked: false },
    { id: 'wash', label: 'Wash all loads', checked: false },
    { id: 'dry', label: 'Dry all loads', checked: false },
    { id: 'fold', label: 'Fold clothes neatly', checked: false },
    { id: 'hang', label: 'Hang items that need it', checked: false },
    { id: 'package', label: 'Package for delivery', checked: false }
  ]);
  
  // Delivery state
  const [arrivedAtDelivery, setArrivedAtDelivery] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState(null);
  
  // Signature state
  const [signatureData, setSignatureData] = useState(null);
  const [signerName, setSignerName] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Notes
  const [notes, setNotes] = useState('');

  // Update item count
  const updateItemCount = (id, delta) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, count: Math.max(0, item.count + delta) } : item
    ));
    const newTotal = items.reduce((sum, item) => 
      item.id === id ? sum + Math.max(0, item.count + delta) : sum + item.count, 0
    );
    setTotalItems(newTotal);
  };

  // Toggle checklist item
  const toggleChecklistItem = (id) => {
    setChecklist(prev => prev.map(item =>
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  // Handle photo upload
  const handlePhotoUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      const photoObj = {
        id: Date.now(),
        url: previewUrl,
        file: file,
        timestamp: new Date().toISOString()
      };

      if (type === 'before') {
        setBeforePhotos(prev => [...prev, photoObj]);
      } else {
        setAfterPhotos(prev => [...prev, photoObj]);
      }
      toast.success('Photo added');
    } catch (error) {
      toast.error('Failed to add photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Signature canvas handlers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#D4AF37';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      if (canvas) {
        setSignatureData(canvas.toDataURL());
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
    }
  };

  // GPS check for arrival
  const handleArrival = (type) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (type === 'pickup') {
            setArrivedAtPickup(true);
            setPickupTime(new Date().toISOString());
            toast.success('Arrived at pickup location');
          } else {
            setArrivedAtDelivery(true);
            setDeliveryTime(new Date().toISOString());
            toast.success('Arrived at delivery location');
          }
        },
        () => {
          // Allow manual confirmation if GPS fails
          if (type === 'pickup') {
            setArrivedAtPickup(true);
            setPickupTime(new Date().toISOString());
          } else {
            setArrivedAtDelivery(true);
            setDeliveryTime(new Date().toISOString());
          }
          toast.info('Location confirmed manually');
        }
      );
    }
  };

  // Complete workflow
  const handleComplete = async () => {
    setLoading(true);
    try {
      // Prepare completion data for the new job-completion API
      const completionData = {
        job_id: booking.id,
        service_type: 'laundry',
        photos: [
          ...beforePhotos.map(p => ({
            photo_data: p.data || p.url,
            photo_type: 'before',
            caption: 'Before laundry'
          })),
          ...afterPhotos.map(p => ({
            photo_data: p.data || p.url,
            photo_type: 'after',
            caption: 'After laundry - completed'
          }))
        ],
        signature_data: signatureData,
        signer_name: signerName,
        completion_notes: `${totalItems} items processed. ${notes || ''}`.trim(),
        customer_satisfied: true,
        checklist_completed: checklist.filter(c => c.checked).length,
        checklist_total: checklist.length,
        arrival_time: pickupTime,
        start_time: pickupTime,
        end_time: deliveryTime || new Date().toISOString()
      };

      // Try to submit to job-completion API (photos & signature upload)
      try {
        const token = localStorage.getItem('auth_token');
        if (token && (beforePhotos.length > 0 || afterPhotos.length > 0 || signatureData)) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/job-completion/complete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(completionData)
          });
        }
      } catch (uploadErr) {
        console.warn('Photo/signature upload failed, continuing with completion:', uploadErr);
      }
      
      toast.success('Laundry job completed successfully!');
      onWorkflowComplete?.(completionData);
      onClose();
    } catch (error) {
      toast.error('Failed to complete job');
    } finally {
      setLoading(false);
    }
  };

  // Navigation
  const goToNextStep = () => {
    const stepOrder = Object.values(LAUNDRY_STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1]);
    }
  };

  const goToPrevStep = () => {
    const stepOrder = Object.values(LAUNDRY_STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };

  // Get step progress
  const getStepProgress = () => {
    const stepOrder = Object.values(LAUNDRY_STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    return ((currentIndex + 1) / stepOrder.length) * 100;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        data-testid="laundry-workflow-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[var(--card)] border border-[var(--border)] rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-4 border-b border-[var(--border)] bg-gradient-to-r from-cyan-600/20 to-blue-600/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/20">
                  <Shirt className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h2 className="font-semibold text-white">Laundry Workflow</h2>
                  <p className="text-xs text-gray-400">{booking?.service_name || 'Laundry Service'}</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Progress</span>
                <span>{Math.round(getStepProgress())}%</span>
              </div>
              <div className="h-2 bg-[var(--background)] rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${getStepProgress()}%` }}
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <AnimatePresence mode="wait">
              {/* OVERVIEW STEP */}
              {currentStep === LAUNDRY_STEPS.OVERVIEW && (
                <motion.div
                  key="overview"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white">Job Overview</h3>
                  
                  {/* Customer Info */}
                  <div className="p-3 rounded-lg bg-[var(--background)] space-y-2">
                    <div className="flex items-center gap-2 text-white">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>{booking?.customer_name || 'Customer'}</span>
                    </div>
                    {booking?.customer_phone && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <Phone className="w-4 h-4" />
                        <span>{booking.customer_phone}</span>
                      </div>
                    )}
                  </div>

                  {/* Pickup Address */}
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                    <p className="text-xs text-green-400 mb-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> PICKUP
                    </p>
                    <p className="text-white">{booking?.address || booking?.pickup_address || 'Address not specified'}</p>
                  </div>

                  {/* Delivery Address (if different) */}
                  {booking?.delivery_address && booking.delivery_address !== booking?.address && (
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <p className="text-xs text-blue-400 mb-1 flex items-center gap-1">
                        <Truck className="w-3 h-3" /> DELIVERY
                      </p>
                      <p className="text-white">{booking.delivery_address}</p>
                    </div>
                  )}

                  {/* Special Instructions */}
                  {booking?.special_instructions && (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <p className="text-xs text-yellow-400 mb-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> SPECIAL INSTRUCTIONS
                      </p>
                      <p className="text-white text-sm">{booking.special_instructions}</p>
                    </div>
                  )}

                  {/* Laundry Details */}
                  {booking?.laundry_info && (
                    <div className="p-3 rounded-lg bg-[var(--background)] space-y-2">
                      <p className="text-xs text-gray-400">LAUNDRY DETAILS</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {booking.laundry_info.load_size && (
                          <div>
                            <span className="text-gray-400">Load Size:</span>
                            <span className="text-white ml-2 capitalize">{booking.laundry_info.load_size}</span>
                          </div>
                        )}
                        {booking.laundry_info.wash_type && (
                          <div>
                            <span className="text-gray-400">Wash Type:</span>
                            <span className="text-white ml-2 capitalize">{booking.laundry_info.wash_type}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* PICKUP STEP */}
              {currentStep === LAUNDRY_STEPS.PICKUP && (
                <motion.div
                  key="pickup"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-green-400" />
                    Pickup Location
                  </h3>

                  <div className="p-4 rounded-lg bg-[var(--background)]">
                    <p className="text-white mb-2">{booking?.address || booking?.pickup_address}</p>
                    
                    {!arrivedAtPickup ? (
                      <Button 
                        onClick={() => handleArrival('pickup')}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <Navigation className="w-4 h-4 mr-2" />
                        Confirm Arrival at Pickup
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 text-green-400 p-3 rounded bg-green-500/10">
                        <CheckCircle className="w-5 h-5" />
                        <span>Arrived at {new Date(pickupTime).toLocaleTimeString()}</span>
                      </div>
                    )}
                  </div>

                  {arrivedAtPickup && (
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <p className="text-sm text-blue-300">
                        Greet the customer and collect all laundry items. 
                        Make sure to count and document everything!
                      </p>
                    </div>
                  )}
                </motion.div>
              )}

              {/* ITEM COUNT STEP */}
              {currentStep === LAUNDRY_STEPS.ITEM_COUNT && (
                <motion.div
                  key="item_count"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Package className="w-5 h-5 text-cyan-400" />
                    Count Items
                  </h3>

                  <div className="space-y-2">
                    {items.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--background)]">
                        <span className="text-white">{item.type}</span>
                        <div className="flex items-center gap-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateItemCount(item.id, -1)}
                            className="h-8 w-8 p-0"
                          >
                            -
                          </Button>
                          <span className="w-8 text-center text-white font-medium">{item.count}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateItemCount(item.id, 1)}
                            className="h-8 w-8 p-0"
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                    <p className="text-cyan-400 font-semibold">Total Items: {totalItems}</p>
                  </div>

                  <div>
                    <Label className="text-gray-400">Notes about items (stains, damages, etc.)</Label>
                    <Textarea
                      value={itemNotes}
                      onChange={(e) => setItemNotes(e.target.value)}
                      placeholder="Note any stains, damages, or special care items..."
                      className="mt-1 bg-[var(--background)]"
                      rows={3}
                    />
                  </div>
                </motion.div>
              )}

              {/* PHOTOS BEFORE STEP */}
              {currentStep === LAUNDRY_STEPS.PHOTOS_BEFORE && (
                <motion.div
                  key="photos_before"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Camera className="w-5 h-5 text-yellow-400" />
                    Before Photos
                  </h3>

                  <p className="text-gray-400 text-sm">
                    Take photos of items before washing (especially any stains or damage)
                  </p>

                  <div className="grid grid-cols-3 gap-2">
                    {beforePhotos.map(photo => (
                      <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                        <img src={photo.url} alt="Before" className="w-full h-full object-cover" />
                      </div>
                    ))}
                    
                    <label className="aspect-square rounded-lg border-2 border-dashed border-[var(--border)] flex flex-col items-center justify-center cursor-pointer hover:border-[var(--primary)] transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e, 'before')}
                        disabled={uploadingPhoto}
                      />
                      {uploadingPhoto ? (
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                      ) : (
                        <>
                          <Camera className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-400 mt-1">Add Photo</span>
                        </>
                      )}
                    </label>
                  </div>

                  <p className="text-xs text-gray-500">
                    {beforePhotos.length} photo(s) taken
                  </p>
                </motion.div>
              )}

              {/* IN PROGRESS STEP */}
              {currentStep === LAUNDRY_STEPS.IN_PROGRESS && (
                <motion.div
                  key="in_progress"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Shirt className="w-5 h-5 text-blue-400" />
                    Laundry Checklist
                  </h3>

                  <div className="space-y-2">
                    {checklist.map(item => (
                      <div
                        key={item.id}
                        onClick={() => toggleChecklistItem(item.id)}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          item.checked ? 'bg-green-500/10 border border-green-500/30' : 'bg-[var(--background)]'
                        }`}
                      >
                        {item.checked ? (
                          <CheckSquare className="w-5 h-5 text-green-400" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                        <span className={item.checked ? 'text-green-300' : 'text-white'}>
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-sm text-blue-300">
                      {checklist.filter(c => c.checked).length} of {checklist.length} tasks completed
                    </p>
                  </div>
                </motion.div>
              )}

              {/* PHOTOS AFTER STEP */}
              {currentStep === LAUNDRY_STEPS.PHOTOS_AFTER && (
                <motion.div
                  key="photos_after"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Image className="w-5 h-5 text-green-400" />
                    After Photos
                  </h3>

                  <p className="text-gray-400 text-sm">
                    Take photos of folded/packaged items ready for delivery
                  </p>

                  <div className="grid grid-cols-3 gap-2">
                    {afterPhotos.map(photo => (
                      <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                        <img src={photo.url} alt="After" className="w-full h-full object-cover" />
                      </div>
                    ))}
                    
                    <label className="aspect-square rounded-lg border-2 border-dashed border-[var(--border)] flex flex-col items-center justify-center cursor-pointer hover:border-[var(--primary)] transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e, 'after')}
                        disabled={uploadingPhoto}
                      />
                      {uploadingPhoto ? (
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                      ) : (
                        <>
                          <Camera className="w-6 h-6 text-gray-400" />
                          <span className="text-xs text-gray-400 mt-1">Add Photo</span>
                        </>
                      )}
                    </label>
                  </div>
                </motion.div>
              )}

              {/* DELIVERY STEP */}
              {currentStep === LAUNDRY_STEPS.DELIVERY && (
                <motion.div
                  key="delivery"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Truck className="w-5 h-5 text-blue-400" />
                    Delivery
                  </h3>

                  <div className="p-4 rounded-lg bg-[var(--background)]">
                    <p className="text-white mb-2">
                      {booking?.delivery_address || booking?.address || 'Same as pickup'}
                    </p>
                    
                    {!arrivedAtDelivery ? (
                      <Button 
                        onClick={() => handleArrival('delivery')}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        <Navigation className="w-4 h-4 mr-2" />
                        Confirm Arrival at Delivery
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 text-blue-400 p-3 rounded bg-blue-500/10">
                        <CheckCircle className="w-5 h-5" />
                        <span>Arrived at {new Date(deliveryTime).toLocaleTimeString()}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* SIGNATURE STEP */}
              {currentStep === LAUNDRY_STEPS.SIGNATURE && (
                <motion.div
                  key="signature"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Pen className="w-5 h-5 text-yellow-400" />
                    Customer Signature
                  </h3>

                  <div>
                    <Label className="text-gray-400">Customer Name</Label>
                    <Input
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      placeholder="Enter customer name"
                      className="mt-1 bg-[var(--background)]"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-400">Signature</Label>
                    <div className="mt-1 border-2 border-dashed border-[var(--border)] rounded-lg overflow-hidden bg-white">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={150}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        className="w-full touch-none cursor-crosshair"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearSignature}
                      className="mt-2"
                    >
                      Clear Signature
                    </Button>
                  </div>

                  <div>
                    <Label className="text-gray-400">Additional Notes</Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any notes about the delivery..."
                      className="mt-1 bg-[var(--background)]"
                      rows={2}
                    />
                  </div>
                </motion.div>
              )}

              {/* COMPLETE STEP */}
              {currentStep === LAUNDRY_STEPS.COMPLETE && (
                <motion.div
                  key="complete"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">Ready to Complete</h3>
                    <p className="text-gray-400 mt-2">Review the job summary below</p>
                  </div>

                  <div className="space-y-2 p-4 rounded-lg bg-[var(--background)]">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Items</span>
                      <span className="text-white">{totalItems}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Before Photos</span>
                      <span className="text-white">{beforePhotos.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">After Photos</span>
                      <span className="text-white">{afterPhotos.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Checklist</span>
                      <span className="text-white">{checklist.filter(c => c.checked).length}/{checklist.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Signature</span>
                      <span className={signatureData ? 'text-green-400' : 'text-yellow-400'}>
                        {signatureData ? 'Collected' : 'Pending'}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handleComplete}
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Complete Laundry Job
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer Navigation */}
          <div className="p-4 border-t border-[var(--border)] flex justify-between">
            <Button
              variant="outline"
              onClick={goToPrevStep}
              disabled={currentStep === LAUNDRY_STEPS.OVERVIEW}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            {currentStep !== LAUNDRY_STEPS.COMPLETE && (
              <Button onClick={goToNextStep}>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LaundryWorkflowModal;
